import type { UnitCode } from '~/graphql/graphql';
import { toStandardUnits } from './unitConversion';

type CalcItemLoadsInput = {
  quantity: { value: number; unit: UnitCode };
  platformProductsLengths: {
    platformProduct: {
      isNominal?: boolean | null;
      nominalThicknessInches?: number | null;
      nominalWidthInches?: number | null;
      actualThicknessInches?: number | null;
      actualWidthInches?: number | null;
      actualLengthInches?: number | null;
      standardUnitsCode?: UnitCode | null;
      unitsPerTruckload: { value: number };
      unitsPerRailCar: { value: number };
    };
    length?: {
      nominalLengthInches?: number | null;
      actualLengthInches?: number | null;
    } | null;
  };
};

export function calcItemLoads(item: CalcItemLoadsInput) {
  const ppl = item.platformProductsLengths;
  const p = ppl?.platformProduct;
  if (!p) return { truckLoads: 0, railcarLoads: 0 };

  const fromUnit = item.quantity.unit ?? 'MBF';
  const stdUnit = p.standardUnitsCode ?? 'MBF';

  const widthInches = p.isNominal
    ? (p.nominalWidthInches ?? p.actualWidthInches ?? 0)
    : (p.actualWidthInches ?? 0);
  const thicknessInches = p.isNominal
    ? (p.nominalThicknessInches ?? p.actualThicknessInches ?? 0)
    : (p.actualThicknessInches ?? 0);
  const lengthInches = p.isNominal
    ? (ppl?.length?.nominalLengthInches ?? ppl?.length?.actualLengthInches ?? 0)
    : (p.actualLengthInches ?? ppl?.length?.actualLengthInches ?? 0);

  if (lengthInches <= 0 || widthInches <= 0 || thicknessInches <= 0) {
    return { truckLoads: 0, railcarLoads: 0 };
  }

  const stdQty = toStandardUnits(
    item.quantity.value,
    fromUnit,
    stdUnit,
    lengthInches,
    widthInches,
    thicknessInches
  );

  return {
    truckLoads:
      p.unitsPerTruckload?.value && p.unitsPerTruckload.value > 0
        ? stdQty / p.unitsPerTruckload.value
        : 0,
    railcarLoads:
      p.unitsPerRailCar?.value && p.unitsPerRailCar.value > 0
        ? stdQty / p.unitsPerRailCar.value
        : 0,
  };
}

type GradeInfoInput = {
  platformProduct: {
    properties?: { grade?: { code?: string | null } | null } | null;
    actualThicknessInches?: number | null;
    actualWidthInches?: number | null;
  };
};

export function getGradeInformation(
  platformProductLength: GradeInfoInput
): string {
  const grade = platformProductLength.platformProduct.properties?.grade?.code;
  const actualThicknessInches =
    platformProductLength.platformProduct.actualThicknessInches;
  const actualWidthInches =
    platformProductLength.platformProduct.actualWidthInches;

  const resp: string[] = [];
  if (grade) {
    resp.push(grade);
  }
  if (actualThicknessInches && actualWidthInches && grade) {
    resp.push('•');
  }

  if (actualThicknessInches && actualWidthInches) {
    resp.push(`${actualThicknessInches} x ${actualWidthInches}`);
  }

  return resp.length === 0 ? 'N/A' : resp.join(' ');
}
